import { useFrame } from "@react-three/fiber";
import React, { useCallback, useMemo, useRef, useState } from "react";
import * as THREE from "three";

function Planet({
  size,
  distance,
  color,
  rotationSpeed,
  orbitSpeed,
  name,
  planetInfo,
  onPlanetClick,
  timeSpeed,
  isPaused,
  isSelected,
}) {
  const planetRef = useRef();
  const planetGroupRef = useRef();
  const [hovered, setHovered] = useState(false);

  useFrame(({ clock }) => {
    if (!isPaused) {
      const time = clock.getElapsedTime() * timeSpeed;

      // Planet rotation on its axis
      if (planetRef.current) {
        planetRef.current.rotation.y += rotationSpeed * timeSpeed;
      }

      // Planet orbit around sun
      if (planetGroupRef.current) {
        planetGroupRef.current.rotation.y = time * orbitSpeed;
      }
    }
  });

  const handleClick = useCallback(
    (event) => {
      event.stopPropagation();
      // console.log("Planet clicked:", name); // Debug log

      // Use the mouse position from the event directly
      // Three.js provides pointer coordinates in event.pointer
      const x = event.pointer
        ? ((event.pointer.x + 1) / 2) * window.innerWidth
        : window.innerWidth / 2;
      const y = event.pointer
        ? ((1 - event.pointer.y) / 2) * window.innerHeight
        : window.innerHeight / 2;

      onPlanetClick(planetInfo, { x, y });
    },
    [planetInfo, onPlanetClick]
  );

  // Enhanced materials with selection highlight
  const planetMaterial = useMemo(() => {
    const material = new THREE.MeshStandardMaterial({
      color: color,
      roughness: 0.8,
      metalness: 0.1,
    });

    if (isSelected || hovered) {
      material.emissive = new THREE.Color(color).multiplyScalar(0.2);
    }

    return material;
  }, [color, isSelected, hovered]);

  // Saturn's rings
  const saturnRings = name === "Saturn" && (
    <mesh rotation={[Math.PI / 2, 0, 0]} receiveShadow>
      <ringGeometry args={[size * 1.2, size * 1.8, 32]} />
      <meshStandardMaterial
        color="#C7B299"
        side={THREE.DoubleSide}
        transparent
        opacity={0.6}
        roughness={0.9}
      />
    </mesh>
  );

  return (
    <group ref={planetGroupRef}>
      <group position={[distance, 0, 0]}>
        <mesh
          ref={planetRef}
          material={planetMaterial}
          onClick={handleClick}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          castShadow
          receiveShadow
        >
          <sphereGeometry args={[size, 32, 32]} />
        </mesh>
        {saturnRings}

        {/* Selection indicator */}
        {isSelected && (
          <mesh>
            <ringGeometry args={[size * 1.1, size * 1.2, 32]} />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={0.5}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
      </group>
    </group>
  );
}
export default Planet;
